import { NgStyle } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SpeechSynthesisService } from '@core/audio/speech-synthesis.service';
import { GameDebugComponent } from '@core/game/debug/game-debug.component';
import { GameContainerComponent } from '@core/game/game-container/game-container.component';
import { GameService } from '@core/game/game.service';
import { DataReaderService } from '@core/lp/data-reader.service';
import { LpProjectSettingsService } from '@core/lp/lp-project-settings.service';
import { Wk8BookService } from '@core/wk8/wk8-book.service';
import { HudButtonComponent } from '@features/hud/hud-button/hud-button.component';
import { HudIconButtonComponent } from '@features/hud/hud-icon-button/hud-icon-button.component';
import { LpCanvasActivityComponent } from '@features/lp/lp-canvas-activity/lp-canvas-activity.component';
import { LpClickActivityComponent } from '@features/lp/lp-click-activity/lp-click-activity.component';
import { LpDragAndDropActivityComponent } from '@features/lp/lp-drag-and-drop-activity/lp-drag-and-drop-activity.component';
import { LpIframeActivityComponent } from '@features/lp/lp-iframe-activity/lp-iframe-activity.component';
import { LpInventoryItemModalComponent } from '@features/lp/lp-inventory/lp-inventory-item-modal/lp-inventory-item-modal.component';
import { LpInventoryComponent } from '@features/lp/lp-inventory/lp-inventory.component';
import { LpMapComponent } from '@features/lp/lp-map/lp-map.component';
import { LpMissionService } from '@features/lp/lp-mission/lp-mission.service';
import { LpNotificationsService } from '@features/lp/lp-notifications/lp-notifications.service';
import { LpStoryActivityComponent } from '@features/lp/lp-story-activity/lp-story-activity.component';
import { LpStoryComponent } from '@features/lp/lp-story/lp-story.component';
import { LpTextComponent } from '@features/lp/lp-text/lp-text.component';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { FADE_ANIMATION, MODAL_ANIMATION } from '@features/styles/animations';
import { StyleSheetSpriteComponent } from '@features/styles/style-sheet-sprite/style-sheet-sprite.component';
import { StylesService } from '@features/styles/styles.service';
import { Wk8LevelActivityComponent } from '@features/wk8/wk8-level-activity/wk8-level-activity.component';
import {
  LpActivity,
  LpCanvasActivity,
  LpClassicMap,
  LpClickActivity,
  LpDragAndDropActivity,
  LpIFrameActivity,
  LpInstance,
  LpStory,
  LpStoryActivity,
} from '@typings/lp/generated';
import { Subscription } from 'rxjs';
import { Uid } from '@typings/id';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Dialog } from '@capacitor/dialog';

@Component({
  selector: 'app-game',
  imports: [
    LpStoryActivityComponent,
    LpDragAndDropActivityComponent,
    LpStoryComponent,
    GameContainerComponent,
    LpClickActivityComponent,
    LpInventoryComponent,
    GameDebugComponent,
    HudIconButtonComponent,
    Wk8LevelActivityComponent,
    HudButtonComponent,
    StyleSheetSpriteComponent,
    LpCanvasActivityComponent,
    LpTextComponent,
    LpIframeActivityComponent,
    LpMapComponent,
    NgStyle,
    LpInventoryItemModalComponent,
  ],
  templateUrl: './game.component.html',
  styleUrl: './game.component.css',
  animations: [FADE_ANIMATION(false), MODAL_ANIMATION(false)],
})
export class GameComponent implements OnInit, OnDestroy {
  isInventoryEnabled = signal(false);
  showInventory = signal(false);
  showFeedbackBar = signal(true);

  protected readonly gameService = inject(GameService);
  protected readonly lpNotificationsService = inject(LpNotificationsService);
  protected readonly stylesService = inject(StylesService);

  readonly #missionService = inject(LpMissionService);
  readonly #lpProjectSettingsService = inject(LpProjectSettingsService);
  readonly #dataReaderService = inject(DataReaderService);
  readonly #route = inject(ActivatedRoute);
  #queryParamsSubscription!: Subscription;
  readonly #lpVariablesService = inject(LpVariablesService); // TODO: remove this and use the one from the game service
  readonly #speechService = inject(SpeechSynthesisService);
  readonly #wk8BookService = inject(Wk8BookService);

  #initialized = false;

  private backButtonSubscription?: PluginListenerHandle;

  constructor() {
    CapacitorApp.addListener('backButton', async () => {
      const { value } = await Dialog.confirm({
        title: 'Bevestig',
        message: `Weet je zeker dat je het spel wilt verlaten en terug wilt gaan naar het profielselectie scherm?`,
        okButtonTitle: 'Ja',
        cancelButtonTitle: 'Nee',
      });

      if (value) {
        window.history.back();
      }
    }).then((r) => (this.backButtonSubscription = r));
  }

  async initializeGame() {
    if (this.#initialized) return;

    await this.#wk8BookService.waitForLoad();

    this.#initialized = true;
    this.gameService.registerGame();
    this.#queryParamsSubscription = this.#route.queryParamMap.subscribe(async (params) => {
      const projectSettings = await this.#lpProjectSettingsService.getSettings();
      this.isInventoryEnabled.set(projectSettings.enableInventory);

      // initialize variables
      await this.#lpVariablesService.waitForInitialization();

      // initialize state
      const stateUid = params.get('state') as Uid | undefined;
      if (stateUid) {
        const state = await this.#dataReaderService.getInstanceByUid(stateUid);
        if (state?.dataType !== 'DebugState') {
          throw new Error(`Expected DebugState, got ${state?.dataType}`);
        }

        console.info('Using state', state);

        for (const v of state.variables) {
          this.#lpVariablesService.set(v.variable.variableRef, v.value);
        }
        await this.gameService.setInventory(state.inventory.map((item) => item.uid as Uid));

        for (const book of state.completedBooks) {
          for (const part of book.sentences.flatMap((s) => s.parts).filter((p) => p.dataType === 'Wk8BookSentencePartFilter')) {
            await this.#wk8BookService.setOpenSentencePartValue(part, 'test');
          }
        }
      }

      // initialize map/mission/activity
      const activityUid = params.get('activity') as Uid | undefined;
      const missionUid = params.get('mission') as Uid | undefined;
      const mapUid = (params.get('map') as Uid | undefined) ?? (projectSettings.woordkr8.defaultMap.uid as Uid);
      if (!activityUid && !missionUid && !mapUid) {
        throw new Error('No activity, mission or map provided in URL');
      }

      if (activityUid) {
        await this.startActivity(activityUid);
      } else if (missionUid) {
        await this.startMission(missionUid);
      } else if (mapUid) {
        await this.startMap(mapUid);
      }
    });
  }

  async ngOnInit() {
    this.showFeedbackBar.set(Capacitor.getPlatform() === 'web');

    this.#speechService.waitForVoicesLoaded().then();
    await this.initializeGame();
  }

  ngOnDestroy() {
    if (this.#queryParamsSubscription) {
      this.#queryParamsSubscription.unsubscribe();
    }
    this.gameService.unregistergame();
    this.backButtonSubscription?.remove();
  }

  async clearStory() {
    await this.gameService.setStory(undefined);
  }

  // user defined typeguards
  isCanvasActivity(instance: LpInstance | undefined): instance is LpCanvasActivity {
    return instance?.dataType === 'CanvasActivity';
  }

  isIFrameActivity(instance: LpInstance | undefined): instance is LpIFrameActivity {
    return instance?.dataType === 'IFrameActivity';
  }

  isClickActivity(instance: LpInstance | undefined): instance is LpClickActivity {
    return instance?.dataType === 'ClickActivity';
  }

  isWk8LevelActivity(instance: LpInstance | undefined): instance is LpClickActivity {
    return instance?.dataType === 'WoordKr8LevelActivity';
  }

  isDragAndDropActivity(instance: LpInstance | undefined): instance is LpDragAndDropActivity {
    return instance?.dataType === 'DragAndDropActivity';
  }

  isStoryActivity(instance: LpInstance | undefined): instance is LpStoryActivity {
    return instance?.dataType === 'StoryActivity';
  }

  isMap(instance: LpInstance | undefined): instance is LpClassicMap {
    return instance?.dataType === 'ClassicMap';
  }

  isStory(instance: LpInstance | undefined): instance is LpStory {
    return instance?.dataType === 'Story';
  }

  cancelOverlay() {
    this.showInventory.set(false);
    this.gameService.setItem(undefined);
    this.lpNotificationsService.next();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // close inventory when pressing escape
    if (event.key === 'Escape') {
      this.cancelOverlay();
    }

    // toggle debug info
    if (event.key === 'd' && event.altKey) {
      event.preventDefault();
      this.gameService.enableDebug.set(!this.gameService.enableDebug());
    }

    // clear db
    if (event.key === 'r' && (event.altKey || event.ctrlKey)) {
      indexedDB.deleteDatabase('database');
      localStorage.removeItem('dataPackageVersion');
      window.location.reload();
      event.preventDefault();
    }
  }

  private async startActivity(activityUid: Uid) {
    const activity = (await this.#dataReaderService.getInstanceByUid(activityUid)) as LpActivity;
    console.info('Starting activity', activity);
    this.#missionService.startActivity(activity);
  }

  private async startMission(missionUid: Uid) {
    const mission = await this.#dataReaderService.getInstanceByUid(missionUid);
    if (mission?.dataType !== 'MissionInfo') {
      throw new Error(`Expected MissionInfo, got ${mission?.dataType}`);
    }
    console.info('Starting mission', mission);
    this.#missionService.start(mission);
  }

  private async startMap(mapUid: Uid) {
    const map = await this.#dataReaderService.getInstanceByUid(mapUid);
    const mapTypes = ['CanvasMap', 'ClassicMap'] as string[];
    if (!mapTypes.includes(map?.dataType ?? '')) {
      throw new Error(`Expected one of ${mapTypes.join(', ')}, got ${map?.dataType}`);
    }
    console.info('Starting map', map);
    await this.gameService.setMap(mapUid);
  }
}
